import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, Users, Crown, Clock, Ban, Plus, Copy, Check, Trash2, Ticket, Settings, X, Star, Globe, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { functionHeaders } from '@/lib/ensureSession';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface UserRow {
  id: string;
  tg_id: number | null;
  email: string | null;
  first_name: string;
  tg_username: string;
  weight_kg: number;
  height_cm: number;
  goal: string;
  activity_level: string;
  subscription_status: string;
  subscription_expires_at: string;
  onboarded: boolean;
  daily_calories: number;
  daily_protein_g: number;
  daily_fat_g: number;
  daily_carbs_g: number;
  custom_calories: number;
  custom_protein_g: number;
  custom_fat_g: number;
  custom_carbs_g: number;
  use_custom_targets: boolean;
  created_at: string;
  updated_at: string;
  last_meal_date: string | null;
  last_steps_date: string | null;
}

interface PromoCode {
  id: string;
  code: string;
  duration_days: number;
  max_uses: number;
  used_count: number;
  description: string;
  is_active: boolean;
  created_at: string;
}

const goalLabel: Record<string, string> = {
  lose: 'Похудение',
  maintain: 'Поддержание',
  gain: 'Набор',
};

const activityLabel: Record<string, string> = {
  sedentary: 'Малоподвижный',
  light: 'Лёгкая',
  moderate: 'Умеренная',
  active: 'Активный',
  very_active: 'Очень активный',
};

const statusColor: Record<string, string> = {
  active: 'text-emerald-glow bg-emerald-glow/10',
  trial: 'text-sky-glow bg-sky-glow/10',
  expired: 'text-danger bg-danger/10',
};

// Метки и активность приходят из admin_user_stats (миграция 027).
// РАНЬШЕ метки лежали в localStorage — то есть существовали только на том
// устройстве, где их поставили: открыл админку с телефона — их нет, почистил
// данные сайта — нет навсегда. Для подопечных, по которым ведётся работа,
// это не годилось.
type UserStats = {
  user_id: string;
  label: string | null;
  note: string | null;
  meals_total: number;
  meals_7d: number;
  meals_30d: number;
  last_meal_at: string | null;
  checkins_7d: number;
  blocked_at: string | null;
  notifications_on: boolean;
  active_days_7d: number;
  avg_cals_day: number;
  avg_prot_day: number;
  avg_fat_day: number;
  avg_carb_day: number;
  last7_count: number;
  last7_cals: number;
  last7_prot: number;
  last7_fat: number;
  last7_carb: number;
  last7_span_days: number;
  steps_7d: number;
  weight_current: number | null;
  weight_last_at: string | null;
  weight_delta_30d: number | null;
  weight_delta_all: number | null;
  weight_count: number;
};

// Ключ старого хранилища — нужен ровно один раз, чтобы перенести
// уже расставленные метки в базу и больше к нему не возвращаться.
const LEGACY_LABELS_KEY = 'admin_user_labels';

type Tab = 'users' | 'mentees' | 'promo';

export default function Admin() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingTargets, setEditingTargets] = useState<UserRow | null>(null);
  const [userStats, setUserStats] = useState<Record<string, UserStats>>({});
  const [detailUser, setDetailUser] = useState<UserRow | null>(null);
  const [messageUser, setMessageUser] = useState<UserRow | null>(null);
  const [platform, setPlatform] = useState<'all' | 'tg' | 'web'>('all');
  const [hideExpired, setHideExpired] = useState(false);
  const [hideInactive, setHideInactive] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  async function checkAdminAndLoad() {
    if (!profile?.tg_id) { nav('/'); return; }
    const { data } = await supabase
      .from('admins')
      .select('tg_id')
      .eq('tg_id', profile.tg_id)
      .maybeSingle();
    if (!data) { nav('/'); return; }
    setIsAdmin(true);
    await loadAll();
  }

  async function loadAll() {
    setLoading(true);
    await Promise.all([loadUsers(), loadPromos(), loadStats()]);
    await migrateLegacyLabels();
    setLoading(false);
  }

  async function loadStats() {
    const { data, error } = await supabase.rpc('admin_user_stats');
    if (error) { console.error('loadStats error', error); return; }
    const map: Record<string, UserStats> = {};
    for (const row of (data as UserStats[]) || []) map[row.user_id] = row;
    setUserStats(map);
  }

  // Разовый перенос меток из localStorage в базу. Старое хранилище чистится
  // сразу после успешного переноса — иначе на другом устройстве оно снова
  // затёрло бы то, что уже лежит на сервере.
  async function migrateLegacyLabels() {
    let legacy: Record<string, string> = {};
    try {
      legacy = JSON.parse(localStorage.getItem(LEGACY_LABELS_KEY) || '{}');
    } catch { return; }
    const entries = Object.entries(legacy);
    if (entries.length === 0) return;
    for (const [userId, label] of entries) {
      // 'beta' больше не используется — при переносе всё становится подопечным,
      // раз уж этих людей когда-то отметили руками.
      await supabase.rpc('admin_set_user_label', {
        target_user_id: userId,
        new_label: 'mentee',
        new_note: label === 'beta' ? 'Перенесено из старой метки «Бета»' : null,
      });
    }
    localStorage.removeItem(LEGACY_LABELS_KEY);
    await loadStats();
  }

  async function loadUsers() {
    const { data, error } = await supabase.rpc('admin_list_users');
    if (!error && data) setUsers(data as UserRow[]);
    else if (error) console.error('loadUsers error', error);
  }

  async function loadPromos() {
    const { data } = await supabase
      .from('promo_codes')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setPromos(data as PromoCode[]);
  }

  async function grantAccess(userId: string, days: number) {
    setActionLoading(userId);
    try {
      const expiry = days >= 36500
        ? new Date(2099, 11, 31).toISOString()
        : new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      await supabase.rpc('admin_update_subscription', {
        target_user_id: userId,
        new_status: 'active',
        new_expires_at: expiry,
      });
      await loadUsers();
    } finally {
      setActionLoading(null);
    }
  }

  async function revokeAccess(userId: string) {
    setActionLoading(userId);
    try {
      await supabase.rpc('admin_update_subscription', {
        target_user_id: userId,
        new_status: 'expired',
        new_expires_at: new Date().toISOString(),
      });
      await loadUsers();
    } finally {
      setActionLoading(null);
    }
  }

  async function toggleMentee(userId: string) {
    const next = userStats[userId]?.label === 'mentee' ? null : 'mentee';
    // Оптимистично: список подопечных перестроится сразу, не дожидаясь сервера.
    setUserStats((s) => ({
      ...s,
      [userId]: { ...(s[userId] as UserStats), user_id: userId, label: next },
    }));
    const { error } = await supabase.rpc('admin_set_user_label', {
      target_user_id: userId,
      new_label: next,
      new_note: userStats[userId]?.note ?? null,
    });
    if (error) console.error('toggleMentee error', error);
    await loadStats();
  }

  async function saveNote(userId: string, note: string) {
    const { error } = await supabase.rpc('admin_set_user_label', {
      target_user_id: userId,
      new_label: userStats[userId]?.label ?? null,
      new_note: note.trim() || null,
    });
    if (error) console.error('saveNote error', error);
    await loadStats();
  }

  const INACTIVE_DAYS = 14;
  const nowMs = Date.now();
  const isWebUser = (u: UserRow) => !u.tg_id;
  const isUserExpired = (u: UserRow) => {
    const e = u.subscription_expires_at ? new Date(u.subscription_expires_at).getTime() : 0;
    return e < nowMs;
  };
  const lastActiveMs = (u: UserRow) => {
    const ds = [u.last_meal_date, u.last_steps_date, u.updated_at]
      .filter(Boolean)
      .map((d) => new Date(d as string).getTime());
    return ds.length ? Math.max(...ds) : new Date(u.created_at).getTime();
  };
  const isUserInactive = (u: UserRow) =>
    nowMs - lastActiveMs(u) > INACTIVE_DAYS * 24 * 60 * 60 * 1000;

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      u.first_name?.toLowerCase().includes(q) ||
      u.tg_username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      String(u.tg_id ?? '').includes(q);
    const matchesPlatform =
      platform === 'all' || (platform === 'web' ? isWebUser(u) : !isWebUser(u));
    const passExpired = !hideExpired || !isUserExpired(u);
    const passInactive = !hideInactive || !isUserInactive(u);
    return matchesSearch && matchesPlatform && passExpired && passInactive;
  });

  const menteeUsers = users.filter((u) => userStats[u.id]?.label === 'mentee');

  // ── Требуют внимания ────────────────────────────────────────────────
  // Три случая, где ещё можно что-то сделать, и каждый раньше приходилось
  // выуживать глазами из общего списка. Отсюда и порядок: сверху то,
  // где окно возможности закрывается быстрее всего.
  const daysUntil = (iso?: string | null) =>
    iso ? Math.floor((new Date(iso).getTime() - Date.now()) / 86400000) : null;
  const daysSince = (iso?: string | null) =>
    iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null;

  const hasAccess = (u: UserRow) =>
    (u.subscription_status === 'active' || u.subscription_status === 'trial') &&
    (!u.subscription_expires_at || new Date(u.subscription_expires_at) > new Date());

  const attention = {
    // Подписка кончается на неделе — успеть поговорить ДО того, как человека
    // развернёт на оплату, а не после.
    expiring: users.filter((u) => {
      const d = daysUntil(u.subscription_expires_at);
      return hasAccess(u) && d !== null && d >= 0 && d <= 7;
    }).sort((a, b) => (daysUntil(a.subscription_expires_at) ?? 0) - (daysUntil(b.subscription_expires_at) ?? 0)),

    // Замолчал 3–7 дней. Именно это окно: раньше трёх — просто выходные,
    // позже семи — уже ушёл, и его ведёт лестница возврата.
    quiet: users.filter((u) => {
      const d = daysSince(userStats[u.id]?.last_meal_at);
      return hasAccess(u) && d !== null && d >= 3 && d <= 7;
    }).sort((a, b) => (daysSince(b.id ? userStats[b.id]?.last_meal_at : null) ?? 0) - (daysSince(userStats[a.id]?.last_meal_at) ?? 0)),

    // Пришёл на днях и не начал. Тот самый случай Насти: девять записей,
    // ушла на шестой день. Ловить надо в первую неделю, дальше поздно.
    stalled: users.filter((u) => {
      const age = daysSince(u.created_at);
      return age !== null && age <= 7 && (userStats[u.id]?.meals_total ?? 0) === 0;
    }),
  };

  const tgCount = users.filter((u) => !isWebUser(u)).length;
  const webCount = users.filter((u) => isWebUser(u)).length;

  const stats = {
    total: users.length,
    active: users.filter((u) => u.subscription_status === 'active').length,
    trial: users.filter((u) => u.subscription_status === 'trial').length,
    expired: users.filter((u) => u.subscription_status === 'expired').length,
  };

  if (!isAdmin && !loading) return null;

  const tabList: { id: Tab; label: string; icon: any; count?: number }[] = [
    { id: 'users', label: 'Все', icon: Users, count: users.length },
    { id: 'mentees', label: 'Подопечные', icon: Star, count: menteeUsers.length },
    { id: 'promo', label: 'Промо', icon: Ticket },
  ];

  return (
    <div className="px-5 pt-4 pb-28 max-w-md mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => nav('/')}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <div>
          <h1 className="font-display text-xl font-bold">Админ-панель</h1>
          <div className="text-xs text-ink-dim">Slava Health</div>
        </div>
      </div>

      {/* Прогон онбординга глазами нового человека — без записи в базу */}
      <button
        onClick={() => nav('/onboarding-test')}
        className="w-full mb-4 py-2.5 rounded-xl bg-bg-card border border-line text-sm text-ink flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
      >
        Пройти онбординг как новый человек
        <span className="text-[10px] text-ink-dim">тест, без сохранения</span>
      </button>

      {/* Табы */}
      <div className="flex gap-1.5 mb-5 bg-bg-card p-1 rounded-xl border border-line">
        {tabList.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
              tab === t.id ? 'bg-emerald-glow text-bg' : 'text-ink-muted'
            }`}
          >
            <t.icon size={12} />
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                tab === t.id ? 'bg-bg/30 text-bg' : 'bg-line text-ink-dim'
              }`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-5">
            <StatCard Icon={Users} label="Всего" value={stats.total} color="text-ink" />
            <StatCard Icon={Crown} label="Активных" value={stats.active} color="text-emerald-glow" />
            <StatCard Icon={Clock} label="Пробных" value={stats.trial} color="text-sky-glow" />
            <StatCard Icon={Ban} label="Истекших" value={stats.expired} color="text-danger" />
          </div>

          <AttentionBlock
            groups={attention}
            onOpen={setDetailUser}
            onWrite={setMessageUser}
          />

          {/* Сегмент площадки */}
          <div className="flex gap-1.5 mb-3 bg-bg-card p-1 rounded-xl border border-line">
            {[
              { id: 'all' as const, label: 'Все', icon: Users, count: users.length },
              { id: 'tg' as const, label: 'Telegram', icon: Send, count: tgCount },
              { id: 'web' as const, label: 'Веб', icon: Globe, count: webCount },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setPlatform(p.id)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-all ${
                  platform === p.id ? 'bg-emerald-glow text-bg' : 'text-ink-muted'
                }`}
              >
                <p.icon size={12} />
                {p.label}
                <span className={`text-[10px] px-1.5 rounded-full ${platform === p.id ? 'bg-bg/30' : 'bg-line text-ink-dim'}`}>
                  {p.count}
                </span>
              </button>
            ))}
          </div>

          {/* Фильтры чистоты списка */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setHideExpired((v) => !v)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                hideExpired ? 'bg-danger/10 text-danger border-danger/30' : 'text-ink-muted border-line'
              }`}
            >
              {hideExpired ? '✓ ' : ''}Скрыть истёкших
            </button>
            <button
              onClick={() => setHideInactive((v) => !v)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                hideInactive ? 'bg-warn/10 text-warn border-warn/30' : 'text-ink-muted border-line'
              }`}
            >
              {hideInactive ? '✓ ' : ''}Скрыть неактивных
            </button>
          </div>

          <input
            className="input mb-2 text-sm"
            placeholder="Поиск по имени, @username, email или ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="text-[11px] text-ink-dim mb-4">
            Показано {filtered.length} из {users.length}
            {hideInactive && ` · неактивные скрыты (>${INACTIVE_DAYS} дн.)`}
          </div>

          {loading ? (
            <div className="text-center text-ink-muted py-10">Загружаем…</div>
          ) : (
            <div className="space-y-3">
              {filtered.map((u) => (
                <UserCard
                  key={u.id}
                  user={u}
                  stat={userStats[u.id]}
                  actionLoading={actionLoading}
                  profileId={profile?.id}
                  onGrant={grantAccess}
                  onRevoke={revokeAccess}
                  onEdit={setEditingTargets}
                  onToggleMentee={toggleMentee}
                  onSaveNote={saveNote}
                  onOpen={setDetailUser}
                />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'mentees' && (
        <MenteesTab
          menteeUsers={menteeUsers}
          stats={userStats}
          actionLoading={actionLoading}
          profileId={profile?.id}
          onGrant={grantAccess}
          onRevoke={revokeAccess}
          onEdit={setEditingTargets}
          onToggleMentee={toggleMentee}
          onSaveNote={saveNote}
          onOpen={setDetailUser}
          allUsers={users}
        />
      )}

      {tab === 'promo' && (
        <PromoTab
          promos={promos}
          loading={loading}
          onRefresh={loadPromos}
          adminId={profile?.id}
        />
      )}

      {/* Окна админки — порталом в <body> (13.09.2026). Внутри экрана их z-50 проигрывал
          нижнему меню с z-30: экран лежит в своём слое наложения, и номер слоя внутри
          него с меню в корне не сравнивается. Меню перекрывало низ окна вместе
          с кнопкой «Отправить» — Слава не мог написать пользователю. */}
      {editingTargets && createPortal(
        <EditTargetsModal
          user={editingTargets}
          onClose={() => setEditingTargets(null)}
          onSaved={() => { setEditingTargets(null); loadUsers(); }}
        />
      , document.body)}

      {detailUser && createPortal(
        <UserDetailModal
          user={detailUser}
          stat={userStats[detailUser.id]}
          onClose={() => setDetailUser(null)}
          onWrite={(u) => { setDetailUser(null); setMessageUser(u); }}
        />
      , document.body)}

      {messageUser && createPortal(
        <MessageModal
          user={messageUser}
          onClose={() => setMessageUser(null)}
        />
      , document.body)}
    </div>
  );
}

// ─── Карточка пользователя ────────────────────────────────────────────────────

function UserCard({
  user: u,
  stat,
  actionLoading,
  profileId,
  onGrant,
  onRevoke,
  onEdit,
  onToggleMentee,
  onSaveNote,
  onOpen,
}: {
  user: UserRow;
  stat?: UserStats;
  actionLoading: string | null;
  profileId?: string;
  onGrant: (id: string, days: number) => void;
  onRevoke: (id: string) => void;
  onEdit: (u: UserRow) => void;
  onToggleMentee: (id: string) => void;
  onSaveNote: (id: string, note: string) => void;
  onOpen: (u: UserRow) => void;
}) {
  const label = stat?.label;
  // Список свёрнут по умолчанию: 43 карточки в полный рост — это полотно,
  // по которому невозможно вести взглядом. Раскрывается тапом по строке.
  const [open, setOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(stat?.note ?? '');
  const [noteOpen, setNoteOpen] = useState(false);
  const daysWithUs = Math.max(0, Math.floor((Date.now() - new Date(u.created_at).getTime()) / 86400000));
  const daysSilent = stat?.last_meal_at
    ? Math.floor((Date.now() - new Date(stat.last_meal_at).getTime()) / 86400000)
    : null;
  return (
    <div className={`card p-4 ${label === 'mentee' ? 'border-warn/30' : ''}`}>
      <div
        onClick={() => setOpen((v) => !v)}
        className="flex items-start justify-between gap-2 mb-2 cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(u); }}
              className="font-medium hover:text-emerald-glow text-left"
            >
              {u.first_name || 'Без имени'}
            </button>
            {u.tg_username && (
              <span className="text-ink-dim text-xs">@{u.tg_username}</span>
            )}
            {label === 'mentee' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-warn/15 text-warn font-medium flex items-center gap-1">
                <Star size={9} /> Подопечный
              </span>
            )}
            {/* Заблокировал бота — до человека не достучаться уведомлением,
                и это надо видеть сразу, а не гадать, почему он не отвечает. */}
            {stat?.blocked_at && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-danger/15 text-danger font-medium flex items-center gap-1">
                <Ban size={9} /> Заблокировал бота
              </span>
            )}
            {!stat?.blocked_at && stat?.notifications_on === false && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-line text-ink-dim font-medium">
                Уведомления off
              </span>
            )}
          </div>
          <div className="text-xs text-ink-dim mt-0.5 flex items-center gap-1 flex-wrap">
            {u.tg_id ? (
              <span className="flex items-center gap-1"><Send size={9} className="text-sky-glow" />TG · {u.tg_id}</span>
            ) : (
              <span className="flex items-center gap-1"><Globe size={9} className="text-emerald-glow" />Веб · {u.email ?? '—'}</span>
            )}
            <span>• {u.onboarded ? 'Онбординг пройден' : 'Не завершил'}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Одна цифра в свёрнутом виде — записи за неделю. Именно по ней
              видно, живой человек или нет, и ради неё раскрывать не нужно. */}
          {!open && (
            <span className={`text-xs tabular-nums ${(stat?.meals_7d ?? 0) > 0 ? 'text-emerald-glow' : 'text-ink-dim'}`}>
              {stat?.meals_7d ?? 0}
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[u.subscription_status] ?? 'text-ink-dim bg-line'}`}>
            {u.subscription_status === 'active' ? 'Активен' :
             u.subscription_status === 'trial' ? 'Пробный' : 'Истёк'}
          </span>
          <ChevronDown
            size={14}
            className={`text-ink-dim transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {open && (<>

      <div className="grid grid-cols-3 gap-1 text-xs text-ink-dim mb-3">
        <div>
          <div className="text-ink-muted">Цель</div>
          <div>{goalLabel[u.goal] ?? '—'}</div>
        </div>
        <div>
          <div className="text-ink-muted">Калории</div>
          <div className="flex items-center gap-1">
            {u.use_custom_targets ? (u.custom_calories ?? '—') : (u.daily_calories ?? '—')}
            {u.use_custom_targets && (
              <span className="text-[8px] bg-sky-glow/20 text-sky-glow px-1 rounded">К</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-ink-muted">Активность</div>
          <div className="text-[10px]">{activityLabel[u.activity_level] ?? '—'}</div>
        </div>
      </div>

      {u.subscription_expires_at && (
        <div className="text-xs text-ink-dim mb-2">
          {u.subscription_status === 'expired' ? 'Истекла' : 'До'}{' '}
          {format(new Date(u.subscription_expires_at), 'd MMM yyyy', { locale: ru })}
        </div>
      )}

      {/* Стаж и активность. Раньше тут была одна дата регистрации, по которой
          нельзя было понять ни сколько человек с нами, ни живой ли он вообще. */}
      <div className="rounded-xl bg-bg/40 border border-line p-2.5 mb-3">
        <div className="grid grid-cols-3 gap-1 text-center">
          <div>
            <div className="text-sm font-bold">{daysWithUs}</div>
            <div className="text-[10px] text-ink-dim">
              {daysWithUs % 10 === 1 && daysWithUs % 100 !== 11 ? 'день с нами' : 'дней с нами'}
            </div>
          </div>
          <div>
            <div className={`text-sm font-bold ${(stat?.meals_7d ?? 0) > 0 ? 'text-emerald-glow' : 'text-ink-dim'}`}>
              {stat?.meals_7d ?? 0}
            </div>
            <div className="text-[10px] text-ink-dim">за 7 дней</div>
          </div>
          <div>
            <div className="text-sm font-bold">{stat?.meals_total ?? 0}</div>
            <div className="text-[10px] text-ink-dim">всего записей</div>
          </div>
        </div>
        {/* КБЖУ. Среднее — по дням, В КОТОРЫЕ ЕЛ, а не делением на 7:
            иначе пропуск в дневнике читался бы как недоедание. */}
        {(stat?.active_days_7d ?? 0) > 0 && (
          <div className="mt-2 pt-2 border-t border-line text-[11px]">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-ink-dim">В среднем за день:</span>
              <span className="font-bold">{stat?.avg_cals_day} ккал</span>
              <span className="text-ink-dim">
                Б{stat?.avg_prot_day} · Ж{stat?.avg_fat_day} · У{stat?.avg_carb_day}
              </span>
              <span className="text-ink-dim">
                (по {stat?.active_days_7d} {stat?.active_days_7d === 1 ? 'дню' : 'дням'})
              </span>
            </div>
            {/* УБРАНО 05.09.2026 по замечанию Славы: строка «Последние 7 приёмов».
                Она показывала СУММУ семи приёмов — число без знаменателя.
                «2499 ккал» это не день и не неделя: семь приёмов могут уложиться
                в сутки, а могут растянуться на три недели. Сравнить не с чем
                ни с нормой, ни между людьми. Слава: «только путает».

                Задумывалась она для тех, кто пишет урывками, и потому несла
                размах в днях. Но тот же сигнал уже дают соседние поля —
                «за 7 дней» и дата последнего приёма, — и читаются однозначно.
                Полезной оставалась только средняя за день выше: у неё есть
                знаменатель (дни, в которые человек ел).

                ⏭ Поля last7_* остались в RPC admin_user_stats как мёртвые.
                Намеренно не трогаю: функцию уже роняли неквалифицированной
                колонкой (029), и ради снятия неиспользуемых колонок лезть
                в неё снова невыгодно. Убрать при следующей плановой правке. */}
          </div>
        )}

        {/* Вес. Дата замера показывается ВСЕГДА: без неё цифра врёт —
            у части людей последний замер двухмесячной давности, а «87.5 кг»
            без пометки читается как сегодняшнее состояние. */}
        {stat?.weight_current != null && (
          <div className="mt-2 pt-2 border-t border-line text-[11px] flex items-baseline gap-1.5 flex-wrap">
            <span className="text-ink-dim">Вес:</span>
            <span className="font-bold">{Number(stat.weight_current).toFixed(1)} кг</span>
            {stat.weight_delta_30d != null && (
              <span className={Number(stat.weight_delta_30d) <= 0 ? 'text-emerald-glow' : 'text-warn'}>
                {Number(stat.weight_delta_30d) > 0 ? '+' : ''}{Number(stat.weight_delta_30d).toFixed(1)} за 30 дн
              </span>
            )}
            {stat.weight_delta_all != null && (
              <span className="text-ink-dim">
                ({Number(stat.weight_delta_all) > 0 ? '+' : ''}{Number(stat.weight_delta_all).toFixed(1)} всего)
              </span>
            )}
            {stat.weight_last_at && (() => {
              const d = Math.floor((Date.now() - new Date(stat.weight_last_at).getTime()) / 86400000);
              return (
                <span className={d > 14 ? 'text-warn' : 'text-ink-dim'}>
                  · замер {d === 0 ? 'сегодня' : `${d} дн. назад`}
                </span>
              );
            })()}
            {stat.weight_count === 1 && (
              <span className="text-ink-dim">· всего один замер</span>
            )}
          </div>
        )}

        <div className="text-[10px] text-ink-dim mt-2 pt-2 border-t border-line flex flex-wrap gap-x-2 gap-y-0.5">
          <span>С {format(new Date(u.created_at), 'd MMM yyyy', { locale: ru })}</span>
          {daysSilent === null ? (
            <span className="text-danger">• Ни одной записи</span>
          ) : (
            <span className={daysSilent >= 7 ? 'text-warn' : ''}>
              • Последняя {daysSilent === 0 ? 'сегодня' : `${daysSilent} дн. назад`}
            </span>
          )}
          {(stat?.checkins_7d ?? 0) > 0 && <span>• Отметок дня: {stat?.checkins_7d}</span>}
          {(stat?.steps_7d ?? 0) > 0 && <span>• Шагов: {stat?.steps_7d.toLocaleString('ru-RU')}</span>}
        </div>
      </div>

      {/* Заметка коуча — то, что нельзя держать в голове по каждому человеку */}
      {noteOpen ? (
        <div className="mb-3">
          <textarea
            className="input text-xs w-full min-h-[60px]"
            placeholder="Заметка: что важно помнить об этом человеке..."
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            autoFocus
          />
          <div className="flex gap-1.5 mt-1.5">
            <button
              onClick={() => { onSaveNote(u.id, noteDraft); setNoteOpen(false); }}
              className="px-2.5 py-1 rounded-lg bg-emerald-glow text-bg text-[11px] font-bold"
            >
              Сохранить
            </button>
            <button
              onClick={() => { setNoteDraft(stat?.note ?? ''); setNoteOpen(false); }}
              className="px-2.5 py-1 rounded-lg bg-line text-ink-muted text-[11px]"
            >
              Отмена
            </button>
          </div>
        </div>
      ) : stat?.note ? (
        <button
          onClick={() => setNoteOpen(true)}
          className="w-full text-left text-xs text-ink-muted bg-warn/5 border border-warn/20 rounded-lg p-2 mb-3"
        >
          {stat.note}
        </button>
      ) : (
        <button
          onClick={() => setNoteOpen(true)}
          className="text-[11px] text-ink-dim mb-3 hover:text-ink-muted"
        >
          + Добавить заметку
        </button>
      )}

      {u.id !== profileId && (
        <>
          {/* Быстрые кнопки доступа */}
          <div className="flex gap-1.5 flex-wrap mb-2">
            <button
              onClick={() => onGrant(u.id, 7)}
              disabled={actionLoading === u.id}
              className="px-2.5 py-1.5 rounded-lg bg-sky-glow/10 text-sky-glow text-xs font-medium hover:bg-sky-glow/20 transition-colors disabled:opacity-50"
            >
              7д
            </button>
            <button
              onClick={() => onGrant(u.id, 30)}
              disabled={actionLoading === u.id}
              className="px-2.5 py-1.5 rounded-lg bg-emerald-glow/10 text-emerald-glow text-xs font-medium hover:bg-emerald-glow/20 transition-colors disabled:opacity-50"
            >
              30д
            </button>
            {/* 2 месяца — для бета-тестеров */}
            <button
              onClick={() => onGrant(u.id, 60)}
              disabled={actionLoading === u.id}
              className="px-2.5 py-1.5 rounded-lg bg-sky-glow/15 text-sky-glow text-xs font-bold hover:bg-sky-glow/25 transition-colors disabled:opacity-50 border border-sky-glow/30"
            >
              🧪 2 мес
            </button>
            {/* 3 месяца — для подопечных */}
            <button
              onClick={() => onGrant(u.id, 90)}
              disabled={actionLoading === u.id}
              className="px-2.5 py-1.5 rounded-lg bg-warn/15 text-warn text-xs font-bold hover:bg-warn/25 transition-colors disabled:opacity-50 border border-warn/30"
            >
              ⭐ 3 мес
            </button>
            <button
              onClick={() => onGrant(u.id, 365)}
              disabled={actionLoading === u.id}
              className="px-2.5 py-1.5 rounded-lg bg-emerald-glow/10 text-emerald-glow text-xs font-medium hover:bg-emerald-glow/20 transition-colors disabled:opacity-50"
            >
              1г
            </button>
            <button
              onClick={() => onGrant(u.id, 36500)}
              disabled={actionLoading === u.id}
              className="px-2.5 py-1.5 rounded-lg bg-warn/10 text-warn text-xs font-medium hover:bg-warn/20 transition-colors disabled:opacity-50"
            >
              ∞
            </button>
            {u.subscription_status === 'active' && (
              <button
                onClick={() => onRevoke(u.id)}
                disabled={actionLoading === u.id}
                className="px-2.5 py-1.5 rounded-lg bg-danger/10 text-danger text-xs font-medium hover:bg-danger/20 transition-colors disabled:opacity-50"
              >
                Отозвать
              </button>
            )}
          </div>

          {/* Метки + настройки */}
          <div className="flex gap-1.5">
            <button
              onClick={() => onToggleMentee(u.id)}
              className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
                label === 'mentee'
                  ? 'bg-warn/20 text-warn border border-warn/40'
                  : 'bg-bg-elevated text-ink-dim border border-line'
              }`}
            >
              <Star size={11} />
              {label === 'mentee' ? 'Подопечный ✓' : 'В подопечные'}
            </button>
            <button
              onClick={() => onEdit(u)}
              className="px-2.5 py-1.5 rounded-lg bg-bg-elevated text-ink-muted border border-line flex items-center justify-center"
            >
              <Settings size={13} />
            </button>
          </div>
        </>
      )}
      </>)}
    </div>
  );
}

// ─── Бета-вкладка ─────────────────────────────────────────────────────────────

function MenteesTab({
  menteeUsers,
  stats,
  actionLoading,
  profileId,
  onGrant,
  onRevoke,
  onEdit,
  onToggleMentee,
  onSaveNote,
  onOpen,
  allUsers,
}: {
  menteeUsers: UserRow[];
  stats: Record<string, UserStats>;
  actionLoading: string | null;
  profileId?: string;
  onGrant: (id: string, days: number) => void;
  onRevoke: (id: string) => void;
  onEdit: (u: UserRow) => void;
  onToggleMentee: (id: string) => void;
  onSaveNote: (id: string, note: string) => void;
  onOpen: (u: UserRow) => void;
  allUsers: UserRow[];
}) {
  const [addSearch, setAddSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const searchResults = addSearch.length > 1
    ? allUsers.filter((u) => {
        const q = addSearch.toLowerCase();
        const notMentee = stats[u.id]?.label !== 'mentee';
        return notMentee && (
          u.first_name?.toLowerCase().includes(q) ||
          u.tg_username?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          String(u.tg_id).includes(q)
        );
      }).slice(0, 5)
    : [];

  // Подопечные сортируются по свежести последней записи: тот, кто пропал,
  // оказывается сверху. Смысл вкладки — заметить, кому нужно внимание,
  // а не пролистывать список по алфавиту.
  const sorted = [...menteeUsers].sort((a, b) => {
    const la = stats[a.id]?.last_meal_at ? new Date(stats[a.id].last_meal_at as string).getTime() : 0;
    const lb = stats[b.id]?.last_meal_at ? new Date(stats[b.id].last_meal_at as string).getTime() : 0;
    return la - lb;
  });

  return (
    <div>
      <button
        onClick={() => setShowSearch((v) => !v)}
        className="w-full py-2.5 mb-4 rounded-xl bg-emerald-glow text-bg font-bold text-sm flex items-center justify-center gap-2"
      >
        <Plus size={15} /> Добавить подопечного
      </button>

      {showSearch && (
        <div className="card p-3 mb-4">
          <input
            className="input text-sm mb-2"
            placeholder="Найти по имени, @username или почте..."
            value={addSearch}
            onChange={(e) => setAddSearch(e.target.value)}
            autoFocus
          />
          {searchResults.length > 0 && (
            <div className="space-y-1.5">
              {searchResults.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 py-1.5">
                  <div className="text-sm">
                    {u.first_name}
                    {u.tg_username && <span className="text-ink-dim text-xs ml-1">@{u.tg_username}</span>}
                  </div>
                  <button
                    onClick={() => { onToggleMentee(u.id); setAddSearch(''); setShowSearch(false); }}
                    className="px-2 py-1 rounded-lg bg-warn/15 text-warn text-[11px] font-medium shrink-0"
                  >
                    ⭐ В подопечные
                  </button>
                </div>
              ))}
            </div>
          )}
          {addSearch.length > 1 && searchResults.length === 0 && (
            <div className="text-xs text-ink-dim text-center py-2">Не найдено или уже в подопечных</div>
          )}
        </div>
      )}

      {menteeUsers.length === 0 && (
        <div className="text-center text-ink-dim py-10 text-sm">
          <Star size={32} className="mx-auto mb-3 opacity-30" />
          Пока никого не отметил.<br />Добавь через кнопку выше.
        </div>
      )}

      {menteeUsers.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Star size={14} className="text-warn" />
            <span className="text-sm font-bold text-warn">Подопечные</span>
            <span className="text-xs text-ink-dim">({menteeUsers.length})</span>
            <button
              onClick={() => menteeUsers.forEach((u) => onGrant(u.id, 90))}
              className="ml-auto text-[11px] px-2.5 py-1 rounded-lg bg-warn/15 text-warn font-medium"
            >
              ⭐ Всем 3 мес
            </button>
          </div>
          <div className="text-[11px] text-ink-dim mb-3">
            Сверху — те, от кого дольше нет записей.
          </div>
          <div className="space-y-3">
            {sorted.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                stat={stats[u.id]}
                actionLoading={actionLoading}
                profileId={profileId}
                onGrant={onGrant}
                onRevoke={onRevoke}
                onEdit={onEdit}
                onToggleMentee={onToggleMentee}
                onSaveNote={onSaveNote}
                onOpen={onOpen}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── EditTargetsModal (без изменений) ─────────────────────────────────────────

function EditTargetsModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [calories, setCalories] = useState(String(user.use_custom_targets ? user.custom_calories : user.daily_calories) || '2000');
  const [protein, setProtein] = useState(String(user.use_custom_targets ? user.custom_protein_g : user.daily_protein_g) || '100');
  const [fat, setFat] = useState(String(user.use_custom_targets ? user.custom_fat_g : user.daily_fat_g) || '70');
  const [carbs, setCarbs] = useState(String(user.use_custom_targets ? user.custom_carbs_g : user.daily_carbs_g) || '250');
  const [activity, setActivity] = useState(user.activity_level || 'moderate');
  const [goal, setGoal] = useState(user.goal || 'maintain');
  const [useCustom, setUseCustom] = useState(user.use_custom_targets ?? false);
  const [saving, setSaving] = useState(false);
  const [autoSync, setAutoSync] = useState(true);

  function recalcCalories(p: string, f: string, c: string) {
    return Math.round((Number(p) || 0) * 4 + (Number(f) || 0) * 9 + (Number(c) || 0) * 4);
  }

  function updateProtein(val: string) { setProtein(val); if (autoSync) setCalories(String(recalcCalories(val, fat, carbs))); }
  function updateFat(val: string) { setFat(val); if (autoSync) setCalories(String(recalcCalories(protein, val, carbs))); }
  function updateCarbs(val: string) { setCarbs(val); if (autoSync) setCalories(String(recalcCalories(protein, fat, val))); }

  const calculatedCalories = recalcCalories(protein, fat, carbs);
  const manualCalories = Number(calories) || 0;
  const hasMismatch = !autoSync && Math.abs(calculatedCalories - manualCalories) > 5;

  async function save() {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_update_user_targets', {
        target_user_id: user.id,
        new_calories: Number(calories) || 2000,
        new_protein_g: Number(protein) || 100,
        new_fat_g: Number(fat) || 70,
        new_carbs_g: Number(carbs) || 250,
        new_activity_level: activity,
        new_goal: goal,
        use_custom: useCustom,
      });
      if (error) { alert('Ошибка: ' + error.message); return; }
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div 
  className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end justify-center p-4" 
  style={{ paddingBottom: 'max(96px, env(safe-area-inset-bottom))' }}
  onClick={onClose}
>
      <div className="bg-bg-card border border-line rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-bg-card flex items-center justify-between p-4 border-b border-line">
          <div>
            <div className="font-display font-bold">Настройки</div>
            <div className="text-xs text-ink-dim">{user.first_name} {user.tg_username && `@${user.tg_username}`}</div>
          </div>
          <button onClick={onClose} className="text-ink-dim hover:text-ink"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="card p-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">Кастомные значения</div>
              <div className="text-xs text-ink-dim">Перебить авто-расчёт</div>
            </div>
            <button
              onClick={() => setUseCustom(!useCustom)}
              className={`w-12 h-6 rounded-full relative transition-colors ${useCustom ? 'bg-emerald-glow' : 'bg-line'}`}
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-bg transition-transform ${useCustom ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div>
            <label className="label mb-2 block">Цель</label>
            <div className="grid grid-cols-3 gap-2">
              {(['lose', 'maintain', 'gain'] as const).map((g) => (
                <button key={g} onClick={() => setGoal(g)}
                  className={`py-2 rounded-lg text-xs font-medium transition-all ${goal === g ? 'bg-emerald-glow text-bg' : 'bg-bg-elevated text-ink-muted border border-line'}`}>
                  {goalLabel[g]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label mb-2 block">Уровень активности</label>
            <div className="space-y-1.5">
              {(['sedentary', 'light', 'moderate', 'active', 'very_active'] as const).map((a) => (
                <button key={a} onClick={() => setActivity(a)}
                  className={`w-full py-2 px-3 rounded-lg text-left text-xs font-medium transition-all ${activity === a ? 'bg-sky-glow/15 text-sky-glow border border-sky-glow/30' : 'bg-bg-elevated text-ink-muted border border-line'}`}>
                  {activityLabel[a]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label">Калории и БЖУ</label>
              <button onClick={() => setAutoSync(!autoSync)}
                className={`text-[10px] px-2 py-1 rounded-full transition-colors ${autoSync ? 'bg-emerald-glow/15 text-emerald-glow' : 'bg-bg-elevated text-ink-dim'}`}>
                {autoSync ? '🔗 Авто-расчёт' : '🔓 Ручной'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-ink-dim mb-1">Калории {autoSync && <span className="text-emerald-glow">(авто)</span>}</div>
                <input type="number" className={`input text-sm py-2 ${autoSync ? 'opacity-60' : ''}`} value={calories} onChange={(e) => setCalories(e.target.value)} readOnly={autoSync} />
              </div>
              <div>
                <div className="text-xs text-ink-dim mb-1">Белки, г</div>
                <input type="number" className="input text-sm py-2" value={protein} onChange={(e) => updateProtein(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-ink-dim mb-1">Жиры, г</div>
                <input type="number" className="input text-sm py-2" value={fat} onChange={(e) => updateFat(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-ink-dim mb-1">Углеводы, г</div>
                <input type="number" className="input text-sm py-2" value={carbs} onChange={(e) => updateCarbs(e.target.value)} />
              </div>
            </div>
            {autoSync && <p className="text-[11px] text-emerald-glow mt-2">✨ Б×4 + Ж×9 + У×4 = {calculatedCalories} ккал</p>}
            {hasMismatch && <p className="text-[11px] text-warn mt-2">⚠️ По формуле должно быть {calculatedCalories} ккал</p>}
            {!useCustom && <p className="text-[11px] text-ink-dim mt-2">ℹ️ Включи «Кастомные значения» чтобы применить</p>}
          </div>
        </div>

        <div className="sticky bottom-0 bg-bg-card border-t border-line p-4 flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-bg-elevated text-ink-muted text-sm font-medium">Отмена</button>
          <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-xl bg-emerald-glow text-bg text-sm font-bold disabled:opacity-50">
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PromoTab (без изменений) ─────────────────────────────────────────────────

function PromoTab({ promos, loading, onRefresh, adminId }: { promos: PromoCode[]; loading: boolean; onRefresh: () => void; adminId?: string; }) {
  const [showCreate, setShowCreate] = useState(false);
  const [code, setCode] = useState('');
  const [days, setDays] = useState('30');
  const [maxUses, setMaxUses] = useState('1');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  function generateCode() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 8; i++) result += chars[Math.floor(Math.random() * chars.length)];
    setCode(result);
  }

  async function createPromo() {
    if (!code.trim()) { alert('Введи код'); return; }
    setCreating(true);
    try {
      const { error } = await supabase.from('promo_codes').insert({
        code: code.trim().toUpperCase(),
        duration_days: Number(days),
        max_uses: Number(maxUses),
        description: description.trim() || null,
        is_active: true,
        created_by: adminId,
      });
      if (error) { if (error.code === '23505') alert('Такой код уже существует'); else alert('Ошибка: ' + error.message); return; }
      setCode(''); setDays('30'); setMaxUses('1'); setDescription(''); setShowCreate(false); onRefresh();
    } finally { setCreating(false); }
  }

  async function deactivate(id: string) { if (!confirm('Деактивировать?')) return; await supabase.from('promo_codes').update({ is_active: false }).eq('id', id); onRefresh(); }
  async function reactivate(id: string) { await supabase.from('promo_codes').update({ is_active: true }).eq('id', id); onRefresh(); }

  function copyLink(code: string) {
    const link = `${window.location.origin}/?promo=${code}`;
    navigator.clipboard.writeText(link).catch(() => { const el = document.createElement('textarea'); el.value = link; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); });
    setCopiedCode(code); setTimeout(() => setCopiedCode(null), 2000);
  }

  return (
    <div>
      <button onClick={() => setShowCreate((v) => !v)} className="w-full py-3 mb-4 rounded-xl bg-emerald-glow text-bg font-bold flex items-center justify-center gap-2">
        <Plus size={16} /> Создать промо-код
      </button>

      {showCreate && (
        <div className="card p-4 mb-4 space-y-3">
          <div>
            <label className="label mb-1 block">Код</label>
            <div className="flex gap-2">
              <input className="input flex-1 text-sm uppercase" placeholder="FRIEND2026" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
              <button onClick={generateCode} className="px-3 py-2 rounded-lg bg-bg-elevated text-xs text-ink-muted">Сгенерировать</button>
            </div>
          </div>
          <div>
            <label className="label mb-1 block">Срок подписки</label>
            <div className="grid grid-cols-4 gap-1">
              {[{ label: '7д', val: '7' }, { label: '30д', val: '30' }, { label: '1г', val: '365' }, { label: '∞', val: '36500' }].map((opt) => (
                <button key={opt.val} onClick={() => setDays(opt.val)} className={`py-2 rounded-lg text-xs font-medium ${days === opt.val ? 'bg-emerald-glow text-bg' : 'bg-bg-elevated text-ink-muted'}`}>{opt.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label mb-1 block">Лимит использований</label>
            <input type="number" className="input text-sm" placeholder="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
          </div>
          <div>
            <label className="label mb-1 block">Заметка (опционально)</label>
            <input className="input text-sm" placeholder="для друзей / партнёрства" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <button onClick={createPromo} disabled={creating || !code.trim()} className="w-full py-2.5 rounded-xl bg-emerald-glow text-bg text-sm font-bold disabled:opacity-50">
            {creating ? 'Создаём…' : 'Создать промо-код'}
          </button>
        </div>
      )}

      {loading ? <div className="text-center text-ink-muted py-10">Загружаем…</div> :
       promos.length === 0 ? <div className="text-center text-ink-dim py-10 text-sm">Промо-кодов пока нет</div> : (
        <div className="space-y-3">
          {promos.map((p) => {
            const usesLeft = p.max_uses - p.used_count;
            const link = `${window.location.origin}/?promo=${p.code}`;
            return (
              <div key={p.id} className={`card p-4 ${!p.is_active ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="font-mono text-base font-bold text-emerald-glow">{p.code}</div>
                    {p.description && <div className="text-xs text-ink-dim mt-0.5">{p.description}</div>}
                  </div>
                  {!p.is_active ? <span className="text-xs px-2 py-0.5 rounded-full bg-danger/10 text-danger">Отключён</span> :
                   usesLeft <= 0 ? <span className="text-xs px-2 py-0.5 rounded-full bg-warn/10 text-warn">Исчерпан</span> :
                   <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-glow/10 text-emerald-glow">Активен</span>}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <div><div className="text-ink-dim">Срок</div><div className="font-medium">{p.duration_days >= 36500 ? '∞' : `${p.duration_days}д`}</div></div>
                  <div><div className="text-ink-dim">Использовано</div><div className="font-medium">{p.used_count} / {p.max_uses}</div></div>
                  <div><div className="text-ink-dim">Осталось</div><div className="font-medium">{Math.max(0, usesLeft)}</div></div>
                </div>
                <div className="bg-bg-elevated rounded-lg p-2 text-[11px] text-ink-muted break-all mb-2">{link}</div>
                <div className="flex gap-2">
                  <button onClick={() => copyLink(p.code)} className="flex-1 py-2 rounded-lg bg-bg-elevated text-xs text-ink-muted flex items-center justify-center gap-1">
                    {copiedCode === p.code ? <><Check size={12} className="text-emerald-glow" /> Скопировано</> : <><Copy size={12} /> Копировать ссылку</>}
                  </button>
                  {p.is_active ? <button onClick={() => deactivate(p.id)} className="px-3 py-2 rounded-lg bg-danger/10 text-danger"><Trash2 size={12} /></button> :
                   <button onClick={() => reactivate(p.id)} className="px-3 py-2 rounded-lg bg-emerald-glow/10 text-emerald-glow text-xs">Включить</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ Icon, label, value, color }: { Icon: typeof Users; label: string; value: number; color: string; }) {
  return (
    <div className="card p-3 flex items-center gap-3">
      <Icon size={18} className={color} />
      <div>
        <div className="text-xs text-ink-muted">{label}</div>
        <div className={`font-display text-xl font-bold ${color}`}>{value}</div>
      </div>
    </div>
  );
}

// ─── Требуют внимания ─────────────────────────────────────────────────────────
// Три случая, где ещё можно что-то сделать. Раньше каждый приходилось выуживать
// глазами из общего списка на 43 человека — то есть на практике не выуживали.

function AttentionBlock({
  groups,
  onOpen,
  onWrite,
}: {
  groups: { expiring: UserRow[]; quiet: UserRow[]; stalled: UserRow[] };
  onOpen: (u: UserRow) => void;
  onWrite: (u: UserRow) => void;
}) {
  const sections = [
    {
      key: 'expiring',
      title: 'Подписка кончается',
      hint: 'Успеть поговорить до того, как развернёт на оплату',
      users: groups.expiring,
      color: 'text-warn',
      bg: 'bg-warn/5 border-warn/20',
      sub: (u: UserRow) => {
        const d = Math.floor((new Date(u.subscription_expires_at).getTime() - Date.now()) / 86400000);
        return d <= 0 ? 'сегодня' : d === 1 ? 'завтра' : `через ${d} дн.`;
      },
    },
    {
      key: 'quiet',
      title: 'Замолчали',
      hint: '3–7 дней без записей — ещё можно вернуть',
      users: groups.quiet,
      color: 'text-sky-glow',
      bg: 'bg-sky-glow/5 border-sky-glow/20',
      sub: () => 'нет записей',
    },
    {
      key: 'stalled',
      title: 'Не начали',
      hint: 'Пришли на неделе и не записали ни одного приёма',
      users: groups.stalled,
      color: 'text-danger',
      bg: 'bg-danger/5 border-danger/20',
      sub: (u: UserRow) => {
        const d = Math.floor((Date.now() - new Date(u.created_at).getTime()) / 86400000);
        return d === 0 ? 'сегодня' : `${d} дн. назад`;
      },
    },
  ].filter((s) => s.users.length > 0);

  if (sections.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="text-sm font-bold mb-2">Требуют внимания</div>
      <div className="space-y-2">
        {sections.map((s) => (
          <div key={s.key} className={`rounded-xl border p-3 ${s.bg}`}>
            <div className="flex items-baseline gap-2 mb-1">
              <span className={`text-xs font-bold ${s.color}`}>{s.title}</span>
              <span className="text-[11px] text-ink-dim">{s.users.length}</span>
            </div>
            <div className="text-[10px] text-ink-dim mb-2">{s.hint}</div>
            <div className="space-y-1">
              {s.users.slice(0, 6).map((u) => (
                <div key={u.id} className="flex items-center gap-2">
                  <button
                    onClick={() => onOpen(u)}
                    className="flex-1 min-w-0 text-left text-xs truncate hover:text-emerald-glow"
                  >
                    {u.first_name || 'Без имени'}
                    {u.tg_username && <span className="text-ink-dim"> @{u.tg_username}</span>}
                  </button>
                  <span className="text-[10px] text-ink-dim shrink-0">{s.sub(u)}</span>
                  {u.tg_id && (
                    <button
                      onClick={() => onWrite(u)}
                      className="text-[10px] px-2 py-0.5 rounded-md bg-bg-elevated border border-line text-ink-muted shrink-0"
                    >
                      Написать
                    </button>
                  )}
                </div>
              ))}
              {s.users.length > 6 && (
                <div className="text-[10px] text-ink-dim pt-1">и ещё {s.users.length - 6}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Карточка человека ────────────────────────────────────────────────────────

type DetailData = {
  days: { date: string; meals: number; calories: number }[];
  weights: { date: string; kg: number }[];
  recent_meals: { at: string; type: string; calories: number; title: string | null }[];
  summary: {
    meals_30d: number; active_days_30d: number; avg_calories: number;
    avg_calories_day_7d: number; active_days_7d: number;
  };
};

function UserDetailModal({
  user: u,
  stat,
  onClose,
  onWrite,
}: {
  user: UserRow;
  stat?: UserStats;
  onClose: () => void;
  onWrite: (u: UserRow) => void;
}) {
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: d, error } = await supabase.rpc('admin_user_detail', { target_user_id: u.id });
      if (error) console.error('admin_user_detail', error);
      setData(d as DetailData);
      setLoading(false);
    })();
  }, [u.id]);

  const maxMeals = Math.max(1, ...(data?.days || []).map((d) => d.meals));
  const weights = data?.weights || [];
  const weightDelta = weights.length > 1
    ? Number(weights[weights.length - 1].kg) - Number(weights[0].kg)
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-line max-h-[88vh] overflow-y-auto">
        <div className="sticky top-0 bg-bg-card border-b border-line px-4 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{u.first_name || 'Без имени'}</div>
            <div className="text-[11px] text-ink-dim truncate">
              {u.tg_username ? `@${u.tg_username}` : u.email ?? '—'}
            </div>
          </div>
          {u.tg_id && (
            <button
              onClick={() => onWrite(u)}
              className="px-2.5 py-1.5 rounded-lg bg-emerald-glow text-bg text-[11px] font-bold shrink-0"
            >
              Написать
            </button>
          )}
          <button onClick={onClose} className="p-1.5 text-ink-dim shrink-0"><X size={16} /></button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center text-ink-dim py-10 text-sm">Загружаю…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className="card p-2">
                  <div className="text-base font-bold">{data?.summary.meals_30d ?? 0}</div>
                  <div className="text-[10px] text-ink-dim">записей за месяц</div>
                </div>
                <div className="card p-2">
                  <div className="text-base font-bold">{data?.summary.active_days_30d ?? 0}<span className="text-ink-dim text-xs">/30</span></div>
                  <div className="text-[10px] text-ink-dim">дней активен</div>
                </div>
                {/* Средний ДЕНЬ за неделю, а не «на запись» (26.09.2026): среднее
                    по приёмам говорило лишь о том, как человек дробит записи —
                    у Нади выходило 83 ккал при реальных 1574 в день. Делим на дни
                    С ЗАПИСЯМИ и подписываем, на скольких днях основана цифра. */}
                <div className="card p-2">
                  <div className="text-base font-bold">{data?.summary.avg_calories_day_7d ?? 0}</div>
                  <div className="text-[10px] text-ink-dim">
                    ккал в день
                    {(data?.summary.active_days_7d ?? 0) > 0
                      ? ` · ${data?.summary.active_days_7d}/7 дн.`
                      : ' · нет записей'}
                  </div>
                </div>
              </div>

              {/*Активность по дням. Пустые дни показаны намеренно —
                  пропуск и есть то, что надо увидеть. */}
              <div className="mb-4">
                <div className="text-xs font-bold mb-2">Записи за 30 дней</div>
                <div className="flex items-end gap-[2px] h-16">
                  {(data?.days || []).map((d) => (
                    <div
                      key={d.date}
                      title={`${format(new Date(d.date), 'd MMM', { locale: ru })}: ${d.meals}`}
                      className="flex-1 rounded-sm bg-emerald-glow/70"
                      style={{ height: `${Math.max(3, (d.meals / maxMeals) * 100)}%`, opacity: d.meals ? 1 : 0.15 }}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[9px] text-ink-dim mt-1">
                  <span>{data?.days?.[0] && format(new Date(data.days[0].date), 'd MMM', { locale: ru })}</span>
                  <span>сегодня</span>
                </div>
              </div>

              {weights.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-xs font-bold">Вес</span>
                    <span className="text-sm font-bold">
                      {Number(weights[weights.length - 1].kg).toFixed(1)} кг
                    </span>
                    {weightDelta !== null && weightDelta !== 0 && (
                      <span className={`text-xs ${weightDelta < 0 ? 'text-emerald-glow' : 'text-warn'}`}>
                        {weightDelta > 0 ? '+' : ''}{weightDelta.toFixed(1)} кг за период
                      </span>
                    )}
                  </div>

                  {/* Одна точка — не динамика. Рисовать по ней линию значит
                      показать ровный график там, где данных нет вовсе. */}
                  {weights.length < 2 ? (
                    <div className="text-[11px] text-ink-dim">
                      Всего один замер — динамики пока нет
                    </div>
                  ) : (() => {
                    const vals = weights.map((w) => Number(w.kg));
                    const min = Math.min(...vals);
                    const max = Math.max(...vals);
                    const span = max - min || 1;
                    const W = 300;
                    const H = 56;
                    // Точки расставлены по РЕАЛЬНЫМ датам, а не равномерно:
                    // иначе перерыв в два месяца выглядел бы как ровный ход.
                    const t0 = new Date(weights[0].date).getTime();
                    const t1 = new Date(weights[weights.length - 1].date).getTime();
                    const tSpan = t1 - t0 || 1;
                    const pts = weights.map((w) => {
                      const x = ((new Date(w.date).getTime() - t0) / tSpan) * W;
                      const y = H - ((Number(w.kg) - min) / span) * (H - 8) - 4;
                      return `${x.toFixed(1)},${y.toFixed(1)}`;
                    });
                    return (
                      <>
                        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
                          <polyline
                            points={pts.join(' ')}
                            fill="none"
                            stroke="currentColor"
                            className="text-emerald-glow"
                            strokeWidth="2"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            vectorEffect="non-scaling-stroke"
                          />
                          {pts.map((pt, i) => {
                            const [x, y] = pt.split(',');
                            return (
                              <circle
                                key={i}
                                cx={x}
                                cy={y}
                                r="2"
                                className="fill-emerald-glow"
                                vectorEffect="non-scaling-stroke"
                              />
                            );
                          })}
                        </svg>
                        <div className="flex justify-between text-[9px] text-ink-dim">
                          <span>{format(new Date(weights[0].date), 'd MMM', { locale: ru })} · {min.toFixed(1)} кг мин</span>
                          <span>{max.toFixed(1)} кг макс · {format(new Date(weights[weights.length - 1].date), 'd MMM', { locale: ru })}</span>
                        </div>
                        <div className="text-[10px] text-ink-dim mt-1">
                          Замеров: {weights.length}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              <div>
                <div className="text-xs font-bold mb-2">Последние приёмы</div>
                {(data?.recent_meals || []).length === 0 ? (
                  <div className="text-xs text-ink-dim">Записей нет</div>
                ) : (
                  <div className="space-y-1.5">
                    {(data?.recent_meals || []).map((m, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className="text-ink-dim shrink-0 w-[52px]">
                          {format(new Date(m.at), 'd MMM', { locale: ru })}
                        </span>
                        <span className="flex-1 min-w-0 truncate">{m.title || '—'}</span>
                        <span className="text-ink-dim shrink-0">{m.calories} ккал</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {stat?.note && (
                <div className="mt-4 text-xs text-ink-muted bg-warn/5 border border-warn/20 rounded-lg p-2">
                  {stat.note}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Написать человеку ────────────────────────────────────────────────────────

function MessageModal({ user: u, onClose }: { user: UserRow; onClose: () => void }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  // Кнопка «Написать Славе» под сообщением (13.09.2026): ответ человека в чат
  // с ботом уходил в пустоту — бот на обычные сообщения молчит. По умолчанию
  // включена; выключать для новостей, где личный аккаунт показывать незачем.
  const [replyButton, setReplyButton] = useState(true);

  async function send() {
    if (!text.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const headers = await functionHeaders();
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-message`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: u.id, text: text.trim(), reply_button: replyButton }),
      });
      const j = await r.json();
      if (j.ok) {
        setResult('Отправлено');
        setText('');
        setTimeout(onClose, 900);
      } else {
        setResult(j.error === 'Forbidden: bot was blocked by the user'
          ? 'Человек заблокировал бота — сообщение не доставить'
          : `Не ушло: ${j.error ?? 'неизвестная ошибка'}`);
      }
    } catch (e) {
      setResult(`Не ушло: ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-line max-h-[90vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]">
        <div className="border-b border-line px-4 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate">Написать: {u.first_name}</div>
            <div className="text-[11px] text-ink-dim">Придёт в чат с ботом @kokorineatbot</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-ink-dim"><X size={16} /></button>
        </div>
        <div className="p-4">
          <textarea
            className="input text-sm w-full min-h-[110px]"
            placeholder="Что написать..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
          <div className="text-[10px] text-ink-dim mt-1">
            Можно жирным: &lt;b&gt;текст&lt;/b&gt;. К сообщению добавится кнопка «Открыть приложение».
          </div>
          <label htmlFor="msg-reply-button" className="flex items-start gap-2 mt-3 text-xs text-ink-muted cursor-pointer">
            <input
              id="msg-reply-button"
              type="checkbox"
              checked={replyButton}
              onChange={(e) => setReplyButton(e.target.checked)}
              className="mt-0.5 accent-emerald-500"
            />
            <span>Кнопка «Написать Славе» — человек ответит тебе в личный Telegram. Выключи для новостей и напоминаний.</span>
          </label>
          {result && (
            <div className={`text-xs mt-2 ${result === 'Отправлено' ? 'text-emerald-glow' : 'text-danger'}`}>
              {result}
            </div>
          )}
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            className="w-full mt-3 py-2.5 rounded-xl bg-emerald-glow text-bg font-bold text-sm disabled:opacity-50"
          >
            {sending ? 'Отправляю…' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>
  );
}
